import React, { useState, useRef } from "react";
import type { Theme } from "../lib/theme";
import type { Division, ShowToast } from "../types";
import * as Icon from "../components/OEIcons";
import { ALL_DIVISIONS, DEFAULT_SUBS } from "../projectData";
import type { OrgCostCodes } from "../hooks/useOrgCostCodes";
import { walkItems } from "../lib/calculations";
import AddressFields from "../components/AddressFields";
import BuildingCodesPanel from "../components/BuildingCodesPanel";
import { DrawingListPanel } from "../components/DrawingListPanel";
import InfoTooltip from "../components/InfoTooltip";
import EscalationRow from "../components/EscalationRow";
import ContingencyPolicyPanel from "../components/ContingencyPolicyPanel";
import type { ContingencyPolicy } from "../lib/contingency";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface PropertiesTabProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  projectName: string;
  setProjectName: (v: string) => void;
  address: string;
  setAddress: (v: string) => void;
  buildingArea: number;
  setBuildingArea: (v: number) => void;
  duration: number;
  setDuration: (v: number) => void;
  overhead: number;
  escalation: import("../lib/escalation").EscalationSettings;
  setEscalation: (v: import("../lib/escalation").EscalationSettings) => void;
  escalationResult: import("../lib/escalation").EscalationResult;
  setOverhead: (v: number) => void;
  profit: number;
  setProfit: (v: number) => void;
  contingency: number;
  setContingency: (v: number) => void;
  contingencyPolicy: ContingencyPolicy;
  setContingencyPolicy: (p: ContingencyPolicy) => void;
  /** The computed totals, for the worked example under the policy switches. */
  final: any;
  markupMode: string;
  setMarkupMode: React.Dispatch<React.SetStateAction<any>>;
  proposals: any[];
  setProposals: React.Dispatch<React.SetStateAction<any[]>>;
  proposalAreas: any;
  setProposalAreas: React.Dispatch<React.SetStateAction<any>>;
  divisions: Division[];
  setDivisionsRaw: React.Dispatch<React.SetStateAction<any[]>>;
  divisionSettings: any[];
  setDivisionSettings: React.Dispatch<React.SetStateAction<any[]>>;
  drawingList: any[];
  setDrawingList: React.Dispatch<React.SetStateAction<any[]>>;
  propSubTab: string;
  setPropSubTab: React.Dispatch<React.SetStateAction<any>>;
  geoQuery: string;
  setGeoQuery: React.Dispatch<React.SetStateAction<string>>;
  jurisdictionInfo: any;
  setJurisdictionInfo: React.Dispatch<React.SetStateAction<any>>;
  divisionMarkups: any;
  setDivisionMarkups: React.Dispatch<React.SetStateAction<any>>;
  divMarkupExpanded: boolean;
  setDivMarkupExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  orgPartners: any[];
  setOrgPartners: React.Dispatch<React.SetStateAction<any[]>>;
  orgPartnerSearch: string;
  setOrgPartnerSearch: React.Dispatch<React.SetStateAction<string>>;
  showOrgPartnerImport: boolean;
  setShowOrgPartnerImport: React.Dispatch<React.SetStateAction<boolean>>;
  orgPartnerImportStep: "upload" | "preview";
  setOrgPartnerImportStep: React.Dispatch<React.SetStateAction<"upload" | "preview">>;
  orgPartnerImportRows: any[];
  setOrgPartnerImportRows: React.Dispatch<React.SetStateAction<any[]>>;
  orgPartnerImportError: string;
  setOrgPartnerImportError: React.Dispatch<React.SetStateAction<string>>;
  isLocked: boolean;
  sumPropAreas: number;
  getPC: (propId: string, proposals: any[]) => any;
  divTotals: any;
  divMarkupableSubtotals: Record<string, number>;
  projectExclusions: string[];
  subSettings: Record<string, any[]>;
  snapshot: () => void;
  addSub: (divId: string) => void;
  removeSub: (divId: string, subId: string) => void;
  renameSub: (divId: string, subId: string, name: string) => void;
  updateSubCostCode: (divId: string, subId: string, costCode: string) => void;
  orgCostCodes: OrgCostCodes;
  setActiveTab: (tab: string) => void;
  fmt: (n: number) => string;
  fmtDec: (n: number) => string;
  costSFCents: boolean;
  user: any;
  cloudStorage: any;
  showToast: ShowToast;
  uid: () => string;
}

export default function PropertiesTab({
  T, isMobile, isTablet,
  projectName, setProjectName,
  address, setAddress,
  buildingArea, setBuildingArea,
  duration, setDuration,
  escalation, setEscalation, escalationResult,
  overhead, setOverhead,
  profit, setProfit,
  contingency, setContingency,
  contingencyPolicy, setContingencyPolicy, final,
  markupMode, setMarkupMode,
  proposals, setProposals,
  proposalAreas, setProposalAreas,
  divisions, setDivisionsRaw,
  divisionSettings, setDivisionSettings,
  drawingList, setDrawingList,
  propSubTab, setPropSubTab,
  geoQuery, setGeoQuery,
  jurisdictionInfo, setJurisdictionInfo,
  divisionMarkups, setDivisionMarkups,
  divMarkupExpanded, setDivMarkupExpanded,
  orgPartners, setOrgPartners,
  orgPartnerSearch, setOrgPartnerSearch,
  showOrgPartnerImport, setShowOrgPartnerImport,
  orgPartnerImportStep, setOrgPartnerImportStep,
  orgPartnerImportRows, setOrgPartnerImportRows,
  orgPartnerImportError, setOrgPartnerImportError,
  isLocked, sumPropAreas, getPC,
  divTotals, divMarkupableSubtotals, projectExclusions, subSettings,
  snapshot, addSub, removeSub, renameSub, updateSubCostCode, orgCostCodes,
  setActiveTab,
  fmt, fmtDec, costSFCents,
  user, cloudStorage, showToast, uid,
}: PropertiesTabProps) {
// Guards: confirm before breaking a standard code/name pairing. Either half
// breaks cross-project consistency — changing the code loses the link, and
// renaming while keeping the code is worse (the code still looks standard but
// now means something else). Value is captured on focus and compared on blur;
// declining reverts. Non-standard entries (custom subs) are never guarded.
const STD_SUBS = orgCostCodes.all;
const ccOrigRef = useRef<{ subId: string; code: string } | null>(null);
const nameOrigRef = useRef<{ subId: string; name: string } | null>(null);

const confirmCostCodeChange = (divId: string, subId: string, newCode: string) => {
  const orig = ccOrigRef.current;
  ccOrigRef.current = null;
  if (!orig || orig.subId !== subId || orig.code === newCode) return;
  const std = STD_SUBS.find(s => s.costCode === orig.code);
  if (!std) return;
  if (!window.confirm(`${orig.code} is the standard cost code for "${std.name}".\n\nChanging it can break cost tracking across projects. Change to "${newCode}" anyway?`)) {
    updateSubCostCode(divId, subId, orig.code);
  }
};

const confirmSubNameChange = (divId: string, subId: string, costCode: string, newName: string) => {
  const orig = nameOrigRef.current;
  nameOrigRef.current = null;
  if (!orig || orig.subId !== subId || orig.name === newName) return;
  // Only guard when this sub is currently sitting on a standard code/name pair.
  const std = STD_SUBS.find(s => s.costCode === costCode && s.name === orig.name);
  if (!std) return;
  if (!window.confirm(`"${orig.name}" is the standard name for cost code ${costCode}.\n\nRenaming it can break cost tracking across projects. Rename to "${newName}" anyway?`)) {
    renameSub(divId, subId, orig.name);
  }
};
return (
<div className="fade-in" style={{padding:isMobile?"14px 16px":isTablet?"20px 24px":"28px 36px",maxWidth:900}}>
  {/* Subtab switching now lives in the Setup group's nav row — a second
      in-page pill row would duplicate it. */}
  <div style={{fontSize:24,fontWeight:700,color:T.borderAccent,marginBottom:4}}>
    {propSubTab==="settings"?"Project Defaults":"Project Info"}
  </div>
  <div style={{fontSize:11,color:T.textWeak,marginBottom:28}}>
    {propSubTab==="settings"
      ? "Markup configuration, division setup, and subcategory cost codes"
      : "Project name, address, packages, size and duration, building codes, and the drawing list"}
  </div>

  {/* ── Project Info subtab ── */}
  {propSubTab==="info"&&<>

  {/* Project Name + Address */}
  <div className="prop-section">
    <div className="prop-label">Project Info</div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
      <div>
        <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Project Name</div>
        <input className="input-field" value={projectName} onChange={e=>{if(!isLocked)setProjectName(e.target.value)}}
          readOnly={isLocked} style={{width:"100%",boxSizing:"border-box" as any}} placeholder="e.g. 123 Main St — Renovation"/>
      </div>
      <div>
        <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Project Address</div>
        <AddressFields
          value={address}
          onChange={v=>{if(!isLocked)setAddress(v)}}
          onGeoQuery={q=>{if(!isLocked)setGeoQuery(q)}}
          readOnly={isLocked}
          T={T}
        />
      </div>
    </div>
  </div>

  {/* Packages — proposals with individual SF areas */}
  <div className="prop-section">
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
      <div className="prop-label" style={{marginBottom:0}}>Packages</div>
      <div style={{fontSize:10,color:T.textWeak,textTransform:"uppercase",letterSpacing:".08em"}}>Area (SF)</div>
    </div>
    <div style={{fontSize:10,color:T.textWeak,marginBottom:10,lineHeight:1.5}}>
      Each package can have its own area for cost-per-SF calculations. Leave blank to share the project's overall area.
    </div>
    {proposals.map(p=>{
      const pc=getPC(p.id,proposals);
      const hasArea=(proposalAreas[p.id]||0)>0;
      return (
        <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,padding:"8px 12px",background:T.bgApp,borderRadius:3,border:`1px solid ${pc.text}22`}}>
          <span style={{width:12,height:12,borderRadius:3,background:pc.text,flexShrink:0,display:"inline-block"}}/>
          <input className="ghost-input" value={p.name} onChange={e=>setProposals(prev=>prev.map(x=>x.id===p.id?{...x,name:e.target.value}:x))}
            style={{flex:1,fontSize:12,color:pc.text}} placeholder="Package name..."/>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <input className="input-field" type="number" value={proposalAreas[p.id]||""}
              placeholder={sumPropAreas>0?"—":buildingArea>0?String(buildingArea):"SF"}
              onChange={e=>setProposalAreas(prev=>({...prev,[p.id]:+e.target.value||0}))}
              style={{width:80,fontSize:10,padding:"3px 6px",textAlign:"right",
                border:hasArea?`1px solid ${T.borderAccent}55`:`1px solid ${T.borderSecondary}`,
                color:hasArea?T.textSecondary:T.textWeak}}/>
            <span style={{fontSize:10,color:T.textMuted,letterSpacing:".08em"}}>SF</span>
          </div>
          <select value={p.color} onChange={e=>setProposals(prev=>prev.map(x=>x.id===p.id?{...x,color:+e.target.value}:x))}
            style={{background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:T.textMuted,fontSize:10,padding:"3px 6px",borderRadius:3,cursor:"pointer"}}>
            {T.proposalColors.map((c,i)=><option key={i} value={i} style={{background:T.bgInput}}>Color {i+1}</option>)}
          </select>
          <button className="del-btn" disabled={proposals.length<=1} style={{opacity:proposals.length<=1?.2:1}}
            onClick={()=>setProposals(prev=>prev.filter(x=>x.id!==p.id))}>x</button>
        </div>
      );
    })}
    {sumPropAreas>0&&(
      <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:6,padding:"6px 12px 0",borderTop:`1px solid ${T.borderPrimary}`,marginTop:4}}>
        <span style={{fontSize:10,color:T.textWeak}}>Total area:</span>
        <span style={{fontSize:12,fontWeight:700,color:T.borderAccent}}>{sumPropAreas.toLocaleString()} SF</span>
      </div>
    )}
    <button className="add-row-btn" style={{marginTop:8}} onClick={()=>setProposals(prev=>[...prev,{id:uid(),name:"New Package",color:prev.length%T.proposalColors.length}])}>+ Add Package</button>
  </div>

  {/* Building Area */}
  <div className="prop-section">
    <div className="prop-label">Building Area</div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
      <div>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
          <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase"}}>Area (SF)</div>
          {sumPropAreas>0&&<span style={{fontSize:10,background:T.bgAccentSubtle,color:T.borderAccent,border:`1px solid ${T.borderAccent}33`,borderRadius:10,padding:"1px 6px",whiteSpace:"nowrap" as any}}>= sum of packages</span>}
        </div>
        {sumPropAreas>0?(
          <div className="input-field" style={{width:"100%",boxSizing:"border-box" as any,color:T.textSecondary,fontSize:14,fontWeight:600,cursor:"default"}}>
            {sumPropAreas.toLocaleString()}
          </div>
        ):(
          <input className="input-field" type="number"
            value={buildingArea} onChange={e=>{if(!isLocked)setBuildingArea(+e.target.value)}}
            readOnly={isLocked}
            style={{width:"100%",boxSizing:"border-box"}}/>
        )}
      </div>
    </div>
    <div style={{marginTop:10,fontSize:10,color:T.textWeak,lineHeight:1.6}}>
      {sumPropAreas>0
        ? "The sum of the package areas above. To set it manually, clear every package SF field."
        : "Drives cost per SF across the estimate. Give packages their own areas above to have this add itself up."}
    </div>
  </div>

  {/* Duration — the single one. The Schedule toolbar edits this same
      number rather than keeping its own, so the two cannot drift. */}
  <div className="prop-section">
    <div className="prop-label">Duration</div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
      <div>
        <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Months</div>
        <input className="input-field" type="number" min={1}
          value={duration} onChange={e=>{if(!isLocked)setDuration(+e.target.value)}}
          readOnly={isLocked}
          style={{width:"100%",boxSizing:"border-box"}}/>
      </div>
    </div>
    <div style={{marginTop:10,fontSize:10,color:T.textWeak,lineHeight:1.6}}>
      The project's overall duration. The Schedule tab reads and sets this same
      figure, so changing it in either place changes both.
    </div>
  </div>

  {/* Building Codes */}
  <div className="prop-section">
    <div className="prop-label">Building Codes &amp; Climate Zone</div>
    <BuildingCodesPanel address={address} geoQuery={geoQuery} T={T} onResult={info=>setJurisdictionInfo(info)}/>
  </div>

  {/* Drawing Set */}
  <div className="prop-section">
    <div className="prop-label">Drawing Set</div>
    <div style={{fontSize:11,color:T.textWeak,marginBottom:14,lineHeight:1.6}}>
      Import the drawing index to log every sheet included in this estimate. The sheet log will appear in the Scope of Work as the Basis of Estimate.
    </div>
    <DrawingListPanel
      drawingList={drawingList}
      onChange={list=>{if(!isLocked)setDrawingList(list);}}
      isLocked={isLocked}
      showToast={showToast}
      T={T}
    />
  </div>



  </>}{/* end propSubTab==="info" */}

  {/* ── Settings subtab ── */}
  {propSubTab==="settings"&&<>

  {/* Markup & Contingency */}
  <div className="prop-section">
    <div className="prop-label" style={{display:"flex",alignItems:"center",gap:6}}>
      Markup & Contingency
      <InfoTooltip T={T} FONT={FONT}
        text={"Overhead: company operating costs (% of the markupable subtotal)\nProfit: margin on top of cost\nContingency: reserve for unknown scope\n\nOverhead and profit are applied to the direct-cost subtotal. Contingency is resolved separately and added after them — the panel below shows the arithmetic on this estimate."}/>
    </div>
    <div style={{display:"flex",gap:4,marginBottom:16}}>
      {([["both","OH + Profit"],["combined","OH&P"],["profit","Profit Only"]] as [string,string][]).map(([mode,label])=>(
        <button key={mode} onClick={()=>{if(!isLocked)setMarkupMode(mode as any)}}
          style={{fontSize:10,padding:"5px 12px",borderRadius:3,border:`1px solid ${T.borderSecondary}`,cursor:isLocked?"default":"pointer",fontFamily:FONT,
            background:markupMode===mode?T.bgAccentLight:"transparent",
            color:markupMode===mode?T.borderAccent:T.textWeak,
            fontWeight:markupMode===mode?600:400}}>
          {label}
        </button>
      ))}
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
      {markupMode==="combined"?(
        <>
          <div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>OH&P %</div>
            <input className="input-field" type="number" step=".5"
              value={overhead} onChange={e=>{if(!isLocked)setOverhead(+e.target.value)}}
              readOnly={isLocked}
              style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
          <div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Contingency %</div>
            <input className="input-field" type="number" step=".5"
              value={contingency} onChange={e=>{if(!isLocked)setContingency(+e.target.value)}}
              readOnly={isLocked}
              style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
        </>
      ):markupMode==="overhead"?(
        <>
          <div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Overhead %</div>
            <input className="input-field" type="number" step=".5"
              value={overhead} onChange={e=>{if(!isLocked)setOverhead(+e.target.value)}}
              readOnly={isLocked}
              style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
          <div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Contingency %</div>
            <input className="input-field" type="number" step=".5"
              value={contingency} onChange={e=>{if(!isLocked)setContingency(+e.target.value)}}
              readOnly={isLocked}
              style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
        </>
      ):markupMode==="profit"?(
        <>
          <div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Profit %</div>
            <input className="input-field" type="number" step=".5"
              value={profit} onChange={e=>{if(!isLocked)setProfit(+e.target.value)}}
              readOnly={isLocked}
              style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
          <div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Contingency %</div>
            <input className="input-field" type="number" step=".5"
              value={contingency} onChange={e=>{if(!isLocked)setContingency(+e.target.value)}}
              readOnly={isLocked}
              style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
        </>
      ):(
        <>
          <div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Overhead %</div>
            <input className="input-field" type="number" step=".5"
              value={overhead} onChange={e=>{if(!isLocked)setOverhead(+e.target.value)}}
              readOnly={isLocked}
              style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
          <div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Profit %</div>
            <input className="input-field" type="number" step=".5"
              value={profit} onChange={e=>{if(!isLocked)setProfit(+e.target.value)}}
              readOnly={isLocked}
              style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
          <div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>Contingency %</div>
            <input className="input-field" type="number" step=".5"
              value={contingency} onChange={e=>{if(!isLocked)setContingency(+e.target.value)}}
              readOnly={isLocked}
              style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
        </>
      )}
    </div>

    <EscalationRow
      T={T} FONT={FONT} fmt={fmt} isLocked={isLocked}
      settings={escalation} setSettings={setEscalation} result={escalationResult}/>

    <ContingencyPolicyPanel
      T={T} FONT={FONT} fmt={fmt} isLocked={isLocked}
      policy={contingencyPolicy} setPolicy={setContingencyPolicy}
      lineDefaultPct={contingency} steps={final?.contingencySteps||[]}/>
  </div>


  {/* ── Org Trade Partner Address Book ── */}
  <div className="prop-section">
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
      <div className="prop-label" style={{marginBottom:0}}>Trade Partner Address Book</div>
      <div style={{display:"flex",gap:8}}>
        <span style={{fontSize:10,color:T.textWeak,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:10,padding:"2px 8px"}}>{orgPartners.length} partner{orgPartners.length!==1?"s":""}</span>
        <button onClick={()=>{setOrgPartnerImportStep("upload");setOrgPartnerImportRows([]);setOrgPartnerImportError("");setShowOrgPartnerImport(true);}}
          style={{fontSize:10,padding:"3px 10px",background:T.bgAccentLight,border:`1px solid ${T.borderAccent}`,color:T.borderAccent,borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600}}>
          ↑ Import CSV / Excel
        </button>
      </div>
    </div>
    <div style={{fontSize:11,color:T.textWeak,marginBottom:10}}>Org-wide partner list available across all projects. Import from Excel, SmartSheet, or ERP exports. Partners appear in the Bids tab dropdown.</div>
    {orgPartners.length>0&&(
      <input className="input-field" placeholder="Search partners..." value={orgPartnerSearch} onChange={e=>setOrgPartnerSearch(e.target.value)}
        style={{width:"100%",marginBottom:10,fontSize:11,boxSizing:"border-box" as any}}/>
    )}
    <div style={{maxHeight:240,overflowY:"auto"}}>
      {orgPartners.filter(p=>!orgPartnerSearch||p.name.toLowerCase().includes(orgPartnerSearch.toLowerCase())||p.trade.toLowerCase().includes(orgPartnerSearch.toLowerCase())).map(p=>(
        <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:T.bgApp,borderRadius:3,border:`1px solid ${T.borderPrimary}`,marginBottom:6}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,fontWeight:600,color:T.textPrimary,marginBottom:2}}>{p.name}</div>
            <div style={{fontSize:10,color:T.textTertiary,display:"flex",gap:10,flexWrap:"wrap" as any}}>
              {p.trade&&<span><Icon.Folder size={10}/> {p.trade}</span>}
              {p.contact&&<span><Icon.User size={10}/> {p.contact}</span>}
              {p.email&&<span>✉ {p.email}</span>}
              {p.phone&&<span>📞 {p.phone}</span>}
            </div>
          </div>
          <button className="del-btn" onClick={()=>setOrgPartners(prev=>prev.filter(x=>x.id!==p.id))} style={{opacity:.6}}>x</button>
        </div>
      ))}
      {orgPartners.length===0&&<div style={{fontSize:11,color:T.textMuted,fontStyle:"italic"}}>No partners in address book yet. Import a CSV or Excel file to get started.</div>}
    </div>
    {orgPartners.length>0&&(
      <button onClick={()=>{
        const rows=[["Name","Trade","Contact","Phone","Email","Notes"],...orgPartners.map(p=>[p.name,p.trade,p.contact,p.phone,p.email,p.notes])];
        const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
        const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);a.download="trade_partners.csv";document.body.appendChild(a);a.click();document.body.removeChild(a);
      }} style={{marginTop:8,fontSize:10,padding:"4px 12px",background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textTertiary,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>
        ↓ Export list as CSV
      </button>
    )}
  </div>

  {/* Per-Division Markup Overrides — collapsed by default */}
  <div className="prop-section">
    <div onClick={()=>setDivMarkupExpanded(v=>!v)}
      style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",userSelect:"none" as any}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:10,color:T.textWeak,transition:"transform .2s",display:"inline-block",transform:divMarkupExpanded?"rotate(0deg)":"rotate(-90deg)"}}>▾</span>
        <div className="prop-label" style={{marginBottom:0}}>Per-Division Markup Override</div>
        {Object.values(divisionMarkups as any).some((m:any)=>m?.overhead!=null||m?.profit!=null)&&(
          <span style={{fontSize:10,padding:"1px 6px",borderRadius:10,background:`${T.borderAccent}20`,color:T.borderAccent,fontWeight:700}}>ACTIVE</span>
        )}
      </div>
      <span style={{fontSize:10,color:T.textWeak}}>Optional</span>
    </div>
    {divMarkupExpanded&&(
      <>
      <div style={{fontSize:10,color:T.textWeak,margin:"10px 0 12px",lineHeight:1.6}}>
        Leave blank to use the global rates above. Enter a specific % to override for individual divisions.
      </div>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
        <thead>
          <tr style={{borderBottom:`1px solid ${T.borderPrimary}`}}>
            <th style={{textAlign:"left",padding:"4px 8px",fontSize:10,color:T.textMuted,letterSpacing:".1em",textTransform:"uppercase",fontWeight:700}}>Division</th>
            <th style={{textAlign:"center",padding:"4px 8px",fontSize:10,color:T.textMuted,letterSpacing:".1em",textTransform:"uppercase",fontWeight:700}}>Subtotal</th>
            {markupMode!=="profit"&&<th style={{textAlign:"center",padding:"4px 8px",fontSize:10,color:T.textMuted,letterSpacing:".1em",textTransform:"uppercase",fontWeight:700}}>{markupMode==="combined"?"OH&P %":"Overhead %"}</th>}
            {(markupMode==="both"||markupMode==="profit")&&<th style={{textAlign:"center",padding:"4px 8px",fontSize:10,color:T.textMuted,letterSpacing:".1em",textTransform:"uppercase",fontWeight:700}}>Profit %</th>}
            <th style={{textAlign:"right",padding:"4px 8px",fontSize:10,color:T.textMuted,letterSpacing:".1em",textTransform:"uppercase",fontWeight:700}}>Effective Markup</th>
          </tr>
        </thead>
        <tbody>
          {divisions.filter(d=>(divTotals[d.id]?.total||0)>0).map(div=>{
            const dm:{overhead?:number|null;profit?:number|null}=divisionMarkups[div.id]||{};
            const divSub=divMarkupableSubtotals[div.id]||0;
            const effOH=dm.overhead!=null?dm.overhead:overhead;
            const effPr=dm.profit!=null?dm.profit:profit;
            const hasOverride=dm.overhead!=null||dm.profit!=null;
            const markupAmt=divSub*((markupMode!=="profit"?effOH:0)+(markupMode==="both"||markupMode==="profit"?effPr:0))/100;
            return(
              <tr key={div.id} style={{borderBottom:`1px solid ${T.borderPrimary}22`,background:hasOverride?`${T.borderAccent}06`:"transparent"}}>
                <td style={{padding:"6px 8px",color:hasOverride?T.borderAccent:T.textTertiary,fontWeight:hasOverride?600:400}}>
                  {div.name}{hasOverride&&<span style={{marginLeft:6,fontSize:10,padding:"1px 5px",borderRadius:10,background:`${T.borderAccent}20`,color:T.borderAccent,fontWeight:700}}>CUSTOM</span>}
                </td>
                <td style={{padding:"6px 8px",textAlign:"center",color:T.textMuted,fontSize:11}}>{fmt(divTotals[div.id]?.total||0)}</td>
                {markupMode!=="profit"&&(
                  <td style={{padding:"4px 6px",textAlign:"center"}}>
                    <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"center"}}>
                      <input type="number" step=".5" min="0" max="100"
                        placeholder={String(overhead)}
                        value={dm.overhead!=null?dm.overhead:""}
                        onChange={e=>setDivisionMarkups(prev=>({...prev,[div.id]:{...prev[div.id],overhead:e.target.value===""?null:+e.target.value}}))}
                        className="input-field" style={{width:64,textAlign:"center",fontSize:11,padding:"3px 6px"}}/>
                      <span style={{fontSize:10,color:T.textMuted}}>%</span>
                    </div>
                  </td>
                )}
                {(markupMode==="both"||markupMode==="profit")&&(
                  <td style={{padding:"4px 6px",textAlign:"center"}}>
                    <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"center"}}>
                      <input type="number" step=".5" min="0" max="100"
                        placeholder={String(profit)}
                        value={dm.profit!=null?dm.profit:""}
                        onChange={e=>setDivisionMarkups(prev=>({...prev,[div.id]:{...prev[div.id],profit:e.target.value===""?null:+e.target.value}}))}
                        className="input-field" style={{width:64,textAlign:"center",fontSize:11,padding:"3px 6px"}}/>
                      <span style={{fontSize:10,color:T.textMuted}}>%</span>
                    </div>
                  </td>
                )}
                <td style={{padding:"6px 8px",textAlign:"right",color:hasOverride?T.borderAccent:T.textMuted,fontWeight:hasOverride?600:400,fontSize:11}}>
                  {fmt(markupAmt)}{hasOverride&&<button onClick={()=>setDivisionMarkups(prev=>{const n={...prev};delete n[div.id];return n;})} title="Clear override" style={{marginLeft:6,background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:10,lineHeight:1}}><Icon.X size={10}/></button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {Object.values(divisionMarkups as any).some((m:any)=>m?.overhead!=null||m?.profit!=null)&&(
        <button onClick={()=>setDivisionMarkups({})} style={{marginTop:8,fontSize:10,padding:"4px 10px",background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>
          Clear all overrides
        </button>
      )}
      </>
    )}
  </div>
  {/* ── Project Exclusions (pointer) ── */}
  <div className="prop-section">
    <div className="prop-label">Project Exclusions</div>
    <div style={{fontSize:11,color:T.textWeak,lineHeight:1.6}}>
      Manage project exclusions from the{" "}
      <button onClick={()=>setActiveTab("summary")} style={{background:"none",border:"none",color:T.borderAccent,cursor:"pointer",fontFamily:FONT,fontSize:11,padding:0,textDecoration:"underline"}}>Summary tab</button>
      {" "}→ Project Exclusions section.{" "}
      Edit your standard library in{" "}
      <button onClick={()=>setActiveTab("settings-team")} style={{background:"none",border:"none",color:T.borderAccent,cursor:"pointer",fontFamily:FONT,fontSize:11,padding:0,textDecoration:"underline"}}>Organization Settings</button>.
    </div>
    {projectExclusions.length>0&&(
      <div style={{marginTop:10,display:"flex",flexWrap:"wrap",gap:5}}>
        {projectExclusions.map((ex,i)=>(
          <span key={i} style={{fontSize:10,padding:"3px 8px",background:T.bgApp,border:`1px solid ${T.borderPrimary}`,borderRadius:10,color:T.textTertiary}}>{ex}</span>
        ))}
      </div>
    )}
  </div>

  <div className="prop-section">
    <div className="prop-label">MasterSpec Divisions</div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
      {ALL_DIVISIONS.map(div=>{
        const activeDiv2=divisions.find(d=>d.id===div.id);
        const on=!!activeDiv2;
        const isEmpty=!activeDiv2||activeDiv2.subcategories.every(s=>s.rows.length===0);
        const handleToggle=()=>{
          if(on){if(!isEmpty)return;snapshot();setDivisionsRaw(prev=>prev.filter(d=>d.id!==div.id));setDivisionSettings(prev=>prev.map(x=>x.id===div.id?{...x,enabled:false}:x));showToast(`Removed ${div.code}`);}
          else{snapshot();
            // Prefer the org catalog so a newly enabled division follows the
            // org's standards, not the built-ins.
            const orgSubs=(orgCostCodes.byDiv[div.id]||[]).map((c,i)=>({id:c.id||`occ_${div.id}_${i}`,name:c.name,costCode:c.costCode}));
            const defSubs=(subSettings[div.id]||(orgSubs.length?orgSubs:DEFAULT_SUBS[div.id])||[]).map(s=>({...s,rows:[]}));const newDiv={id:div.id,code:div.code,name:div.name,subcategories:defSubs};const order=ALL_DIVISIONS.map(d=>d.id);setDivisionsRaw(prev=>{const next=[...prev,newDiv];next.sort((a,b)=>order.indexOf(a.id)-order.indexOf(b.id));return next;});setDivisionSettings(prev=>prev.map(x=>x.id===div.id?{...x,enabled:true}:x));showToast(`Added ${div.code} — ${div.name}`);}
        };
        return (
          <div key={div.id} onClick={on&&!isEmpty?undefined:handleToggle}
            style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:3,cursor:on&&!isEmpty?"not-allowed":"pointer",background:on?T.bgApp:T.bgSidebar,border:`1px solid ${on?T.borderSecondary:T.borderPrimary}`,transition:"all .2s"}}>
            <div className="toggle-switch" style={{background:on?T.borderAccent:T.borderSecondary,flexShrink:0}}>
              <div className="toggle-thumb" style={{left:on?"16px":"2px"}}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:10,color:on?T.textSubLabel:T.textMuted,letterSpacing:".1em",fontWeight:700}}>{div.code}</div>
              <div style={{fontSize:11,color:on?T.textPrimary:T.textMuted}}>{div.name}</div>
            </div>
            {on&&!isEmpty&&<span style={{fontSize:10,color:T.costStatus["Needs Review"].badgeText,flexShrink:0}}>has data</span>}
            {on&&isEmpty&&<span style={{fontSize:10,color:T.textWeak,flexShrink:0}}>click to remove</span>}
          </div>
        );
      })}
    </div>
  </div>
  <div className="prop-section">
    <div className="prop-label">Subcategory Cost Codes</div>
    {divisions.map(div=>(
      <div key={div.id} style={{marginBottom:16}}>
        <div style={{fontSize:10,color:T.borderAccent,letterSpacing:".1em",marginBottom:6,textTransform:"uppercase",fontWeight:600}}>{div.code} — {div.name}</div>
        {div.subcategories.length===0?<div style={{fontSize:10,color:T.textMuted,fontStyle:"italic",paddingLeft:8,marginBottom:6}}>No subcategories</div>
        :div.subcategories.map(sub=>{
          const ic=[...walkItems(sub.rows)].length;
          return (
            <div key={sub.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,paddingLeft:8}}>
              <input value={sub.costCode||""} onChange={e=>updateSubCostCode(div.id,sub.id,e.target.value)} placeholder="00-000"
                onFocus={()=>{ccOrigRef.current={subId:sub.id,code:sub.costCode||""};}}
                onBlur={e=>confirmCostCodeChange(div.id,sub.id,e.target.value)}
                style={{background:T.bgApp,border:`1px solid ${T.borderPrimary}`,color:T.borderAccent,fontSize:11,padding:"3px 8px",borderRadius:3,width:72,fontFamily:FONT,letterSpacing:".06em"}}/>
              <input value={sub.name} onChange={e=>renameSub(div.id,sub.id,e.target.value)}
                onFocus={()=>{nameOrigRef.current={subId:sub.id,name:sub.name};}}
                onBlur={e=>confirmSubNameChange(div.id,sub.id,sub.costCode||"",e.target.value)}
                style={{background:T.bgApp,border:`1px solid ${T.borderPrimary}`,color:T.textSecondary,fontSize:11,padding:"3px 8px",borderRadius:3,flex:1,fontFamily:FONT}}/>
              <span style={{fontSize:10,color:T.textTertiary,minWidth:50,textAlign:"right"}}>{ic} item{ic!==1?"s":""}</span>
              <button className="del-btn" disabled={ic>0} style={{opacity:ic>0?.2:.5}} onClick={()=>{snapshot();removeSub(div.id,sub.id);}}>x</button>
            </div>
          );
        })}
        <button className="add-row-btn" style={{fontSize:10,marginLeft:8,marginTop:4}} onClick={()=>addSub(div.id)}>+ Add Subcategory</button>
      </div>
    ))}
  </div>
  </>}{/* end propSubTab==="settings" */}

</div>
);
}
